// Fully responsive Mobile App Development component
import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const sections = [
  {
    title: "Innovative App Design",
    text: `We craft visually stunning and user-friendly mobile apps that reflect your brand and engage users. Our design-first approach ensures every app provides an intuitive experience while standing out in the market.`,
    img: "../../inovative.png",
  },
  {
    title: "Cross-Platform Compatibility",
    text: `Our mobile apps work seamlessly across iOS and Android devices. We ensure responsive layouts, smooth performance, and consistent functionality so your users have a flawless experience on any device.`,
    img: "../../cross.jpg",
    reverse: true,
  },
  {
    title: "Dynamic Features",
    text: `From push notifications to real-time updates and interactive elements, our apps are built with dynamic functionality to keep users engaged. We leverage the latest technologies to make your app both powerful and intuitive.`,
    img: "../../mobile.jpg",
  },
  {
    title: "Custom Solutions",
    text: `Every business has unique needs. We develop fully customized mobile apps tailored to your requirements, offering scalable, secure, and feature-rich solutions that drive growth and enhance user satisfaction.`,
    img: "../../custom.jpg",
    reverse: true,
  },
];

const ScrollSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    viewport={{ once: false, amount: 0.3 }}
  >
    {children}
  </motion.div>
);

export default function MobileAppDevelopmentTailwind() {
  const handleScroll = () => {
    document.getElementById("mobile-sections").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="font-poppins text-gray-900 overflow-x-hidden scroll-smooth relative">
      {/* Fixed Background */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url('../../mobile.jpg')` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Hero Section */}
      <ScrollSection>
        <section className="relative h-[90vh] sm:h-screen flex flex-col items-center justify-center text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-wider mb-5 text-white drop-shadow-lg"
          >
            Mobile App Development
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-base sm:text-lg md:text-xl max-w-xl sm:max-w-2xl text-gray-300 leading-relaxed"
          >
            Delivering powerful, intuitive, and customized mobile apps for your business.
          </motion.p>

          {/* Scroll Arrow */}
          <motion.div
            className="absolute bottom-10 sm:bottom-20 cursor-pointer animate-bounce text-red-600"
            onClick={handleScroll}
            whileHover={{ scale: 1.15 }}
          >
            <ChevronDown size={36} className="sm:size-[42px]" />
          </motion.div>
        </section>
      </ScrollSection>

      {/* Sections */}
      <div id="mobile-sections" className="max-w-6xl mx-auto py-16 sm:py-20 md:py-24 px-4 sm:px-6 md:px-10 space-y-20 sm:space-y-24 relative z-10">
        {sections.map(({ title, text, img, reverse }, idx) => (
          <ScrollSection key={idx}>
            <div className={`flex flex-col md:flex-row items-center gap-10 sm:gap-12 ${reverse ? "md:flex-row-reverse" : ""}`}>
              {/* Text Card */}
              <motion.div
                className="flex-1 bg-white/90 backdrop-blur-[14px] p-6 sm:p-8 md:p-10 rounded-[20px] shadow-[0_10px_25px_rgba(209,17,17,0.2)] hover:shadow-[0_14px_35px_rgba(209,17,17,0.35)] transition-all duration-300"
                whileHover={{ y: -5 }}
              >
                <h2 className="text-2xl sm:text-3xl md:text-[38px] font-bold mb-4 relative after:block after:w-20 after:h-1 after:bg-red-600 after:rounded mt-2">
                  {title}
                </h2>
                <p className="text-sm sm:text-base md:text-[17px] leading-relaxed text-gray-700">
                  {text}
                </p>
              </motion.div>

              {/* Image */}
              <motion.div
                className="flex-1 text-center"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <img
                  src={img}
                  alt={title}
                  className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg rounded-[20px] shadow-[0_12px_30px_rgba(209,17,17,0.2)]"
                />
              </motion.div>
            </div>
          </ScrollSection>
        ))}
      </div>

      {/* Bounce Animation */}
      <style>{`
        @keyframes bounce {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        .animate-bounce {
          animation: bounce 1.8s infinite;
        }
      `}</style>
    </div>
  );
}